import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { Search, Star, MapPin, ShieldCheck, Settings, Anchor, Globe, X, UserRound } from "lucide-react";
import { api, fmtErr, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canSeeMarketplace, canManageMarketplace } from "../../lib/marketplaceAccess";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { ReviewList } from "../../components/SupplierReview";
import { toast } from "sonner";

const RATING_OPTIONS = [4.5, 4, 3];

// Resolve an image reference: external http(s) URLs (demo seed) render
// directly; object-storage paths go through the authenticated /files route.
const imgSrc = (p) => (p && /^https?:\/\//i.test(p) ? p : `${API}/files/${p}`);

function Stars({ value = 0 }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-500" title={`${value} / 5`}>
      <Star className="h-3.5 w-3.5 fill-amber-400 stroke-amber-400" />
      <span className="text-xs font-medium text-zinc-700">{value ? value.toFixed(1) : "New"}</span>
    </span>
  );
}

function SupplierCard({ s, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(s)}
      data-testid={`supplier-card-${s.id}`}
      className="group text-left rounded-2xl border border-zinc-200 bg-white overflow-hidden hover:border-zinc-300 hover:shadow-sm transition-all flex flex-col"
    >
      <div className="h-24 bg-gradient-to-br from-sky-100 to-zinc-100 relative">
        {s.cover_image && (
          <img src={imgSrc(s.cover_image)} alt="" className="w-full h-full object-cover" />
        )}
        {s.featured && (
          <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-ink text-white px-2 py-0.5 rounded-full">Featured</span>
        )}
        {s.supplier_mode === "offline" && (
          <span data-testid={`offline-badge-${s.id}`} className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wider bg-zinc-600 text-white px-2 py-0.5 rounded-full">Offline</span>
        )}
        {s.is_demo && (
          <span data-testid={`demo-ribbon-${s.id}`} className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full shadow-sm">Demo</span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-3 -mt-9">
          <div className="h-14 w-14 rounded-xl bg-white ring-1 ring-zinc-200 shadow-sm overflow-hidden shrink-0 flex items-center justify-center">
            {s.logo ? (
              <img src={imgSrc(s.logo)} alt="" className="h-full w-full object-cover" />
            ) : (
              <Anchor className="h-6 w-6 text-zinc-400" />
            )}
          </div>
          <div className="pt-9 min-w-0">
            <div className="font-display text-base truncate">{s.company_name}</div>
            <div className="text-xs text-zinc-500 flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3" /> {[s.city, s.country].filter(Boolean).join(", ") || "—"}
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Stars value={s.average_rating} />
          {s.review_count > 0 && <span className="text-[11px] text-zinc-400">({s.review_count})</span>}
          {s.emergency_247 && (
            <span className="ml-auto text-[10px] text-rose-600 font-medium inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> 24/7
            </span>
          )}
        </div>
        {s.description && <p className="mt-2 text-xs text-zinc-600 line-clamp-2">{s.description}</p>}
        <div className="mt-3 flex flex-wrap gap-1">
          {(s.categories || []).slice(0, 3).map((c) => (
            <Badge key={c.id} variant="secondary" className="text-[10px] font-normal">{c.name}</Badge>
          ))}
          {(s.specialties || []).slice(0, 2).map((sp) => (
            <Badge key={sp.id} className="text-[10px] font-normal bg-sky-50 text-sky-700 hover:bg-sky-50">{sp.name}</Badge>
          ))}
        </div>
      </div>
    </button>
  );
}

export function SupplierDetail({ supplier, onClose }) {
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    if (!supplier?.id) { setReviews(null); return; }
    api.get(`/marketplace/suppliers/${supplier.id}/reviews`)
      .then(({ data }) => setReviews(data))
      .catch(() => setReviews([]));
  }, [supplier?.id]);
  if (!supplier) return null;
  const s = supplier;
  return (
    <Dialog open={!!supplier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto p-0" data-testid="supplier-detail">
        <DialogTitle className="sr-only">{s.company_name}</DialogTitle>
        <DialogDescription className="sr-only">Marketplace supplier details</DialogDescription>
        <div className="h-32 bg-gradient-to-br from-sky-100 to-zinc-100 relative">
          {s.cover_image && <img src={imgSrc(s.cover_image)} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="px-6 pb-6">
          <div className="flex items-start gap-4 -mt-10">
            <div className="h-20 w-20 rounded-2xl bg-white ring-1 ring-zinc-200 shadow overflow-hidden flex items-center justify-center shrink-0">
              {s.logo ? <img src={imgSrc(s.logo)} alt="" className="h-full w-full object-cover" /> : <Anchor className="h-8 w-8 text-zinc-400" />}
            </div>
            <div className="pt-10 min-w-0">
              <h2 className="font-display text-2xl">{s.company_name}</h2>
              <div className="text-sm text-zinc-500 flex items-center gap-2">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[s.city, s.country].filter(Boolean).join(", ") || "—"}</span>
                <Stars value={s.average_rating} />
                {s.supplier_mode === "offline" && (
                  <Badge variant="secondary" className="text-[10px] bg-zinc-100 text-zinc-600" data-testid="detail-offline-badge">Offline</Badge>
                )}
              </div>
            </div>
          </div>

          {s.contact_person && (
            <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm flex items-center gap-2 flex-wrap" data-testid="supplier-contact-person">
              {s.contact_person.picture
                ? <img src={s.contact_person.picture} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
                : <UserRound className="h-4 w-4 text-sky-600 shrink-0" />}
              <span className="text-sky-900">Your contact person: <strong>{s.contact_person.name}</strong></span>
              {s.contact_person.email && <span className="text-sky-700 text-xs">{s.contact_person.email}</span>}
            </div>
          )}

          {s.description && <p className="mt-4 text-sm text-zinc-700">{s.description}</p>}

          <div className="mt-4 flex flex-wrap gap-1.5">
            {(s.categories || []).map((c) => <Badge key={c.id} variant="secondary">{c.name}</Badge>)}
            {(s.specialties || []).map((sp) => <Badge key={sp.id} className="bg-sky-50 text-sky-700 hover:bg-sky-50">{sp.name}</Badge>)}
          </div>

          {(s.services || []).length > 0 && (
            <section className="mt-6">
              <h3 className="font-display text-lg mb-2">Services</h3>
              <div className="space-y-2">
                {s.services.map((svc, i) => (
                  <div key={i} className="rounded-lg border border-zinc-200 p-3" data-testid={`detail-service-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm">{svc.name}</div>
                      {svc.starting_price != null && <div className="text-sm text-zinc-600">from {svc.currency || ""} {svc.starting_price}</div>}
                    </div>
                    {svc.description && <p className="text-xs text-zinc-500 mt-1">{svc.description}</p>}
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                      {svc.emergency_available && <Badge variant="outline">Emergency</Badge>}
                      {svc.mobile_service && <Badge variant="outline">Mobile</Badge>}
                      {svc.estimated_duration && <Badge variant="outline">{svc.estimated_duration}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            {s.website && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Website</div>
                <a href={s.website} target="_blank" rel="noreferrer" className="text-sky-700 inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{s.website}</a></div>
            )}
            {(s.languages || []).length > 0 && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Languages</div>{s.languages.join(", ")}</div>
            )}
            {s.address && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Address</div>{s.address}</div>
            )}
            {s.maps_url && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Map</div>
                <a href={s.maps_url} target="_blank" rel="noreferrer" className="text-sky-700 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Open in Maps</a></div>
            )}
            {s.hourly_rate != null && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Hourly rate</div>{s.hourly_rate}</div>
            )}
            {s.response_time && (
              <div><div className="text-xs text-zinc-400 uppercase tracking-wider">Response time</div>{s.response_time}</div>
            )}
          </div>

          {s.contacts_hidden && (
            <section className="mt-6" data-testid="detail-contacts-hidden">
              <h3 className="font-display text-lg mb-2">Contact</h3>
              <p className="text-sm text-zinc-500">
                This supplier hasn't claimed their Seavanto account yet, so contact details are
                hidden. Invite them to quote on a task and they'll be prompted to join.
              </p>
            </section>
          )}

          {(s.contacts || []).length > 0 && (
            <section className="mt-6">
              <h3 className="font-display text-lg mb-2">Contact</h3>
              {s.contacts.map((c, i) => (
                <div key={i} className="text-sm text-zinc-600">
                  <span className="font-medium text-zinc-800">{c.name}</span>{c.position ? ` · ${c.position}` : ""}
                  {c.email && <> · {c.email}</>}{c.phone && <> · {c.phone}</>}
                  {c.whatsapp && <Badge variant="outline" className="ml-2 text-[10px] text-emerald-700 border-emerald-200">WhatsApp</Badge>}
                </div>
              ))}
            </section>
          )}

          {(s.bank_name || s.bank_iban || s.bank_account_number) && (
            <section className="mt-6" data-testid="detail-bank">
              <h3 className="font-display text-lg mb-2">Bank details</h3>
              <div className="rounded-lg border border-zinc-200 p-3 text-sm text-zinc-700 space-y-1">
                {s.bank_name && <div><span className="text-zinc-400">Bank</span> · {s.bank_name}</div>}
                {s.bank_account_holder && <div><span className="text-zinc-400">Account holder</span> · {s.bank_account_holder}</div>}
                {s.bank_account_number && <div><span className="text-zinc-400">Account no.</span> · {s.bank_account_number}</div>}
                {s.bank_iban && <div><span className="text-zinc-400">IBAN</span> · {s.bank_iban}</div>}
                {s.bank_bic_swift && <div><span className="text-zinc-400">BIC / SWIFT</span> · {s.bank_bic_swift}</div>}
              </div>
            </section>
          )}

          <section className="mt-6" data-testid="detail-reviews">
            <h3 className="font-display text-lg mb-2">Reviews{reviews?.length ? ` (${reviews.length})` : ""}</h3>
            <ReviewList
              reviews={reviews || []}
              emptyText="No written reviews yet — ratings appear here once yachts confirm payments and rate their jobs."
            />
          </section>

          <div className="mt-6 rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-500">
            Job requests & quotations are coming soon — for now you can reach this supplier using the contact details above.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Marketplace() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ category: "", specialty: "", country: "", emergency: false, min_rating: 0 });
  const [active, setActive] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/marketplace/categories"),
      api.get("/marketplace/specialties"),
    ]).then(([c, s]) => { setCategories(c.data); setSpecialties(s.data); }).catch(() => {});
  }, []);

  // Pre-filter by system when arriving from a task's "Find suppliers" link
  // (?category=<system name>) — resolve the name to its category id.
  useEffect(() => {
    const catName = searchParams.get("category");
    if (catName && categories.length) {
      const match = categories.find((c) => c.name === catName);
      if (match) setFilters((f) => ({ ...f, category: match.id }));
    }
  }, [searchParams, categories]);

  const fetchSuppliers = useMemo(() => async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filters.category) params.category = filters.category;
      if (filters.specialty) params.specialty = filters.specialty;
      if (filters.country.trim()) params.country = filters.country.trim();
      if (filters.emergency) params.emergency = true;
      if (filters.min_rating) params.min_rating = filters.min_rating;
      const { data } = await api.get("/marketplace/suppliers", { params });
      setSuppliers(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  }, [q, filters]);

  useEffect(() => {
    const t = setTimeout(fetchSuppliers, 250);
    return () => clearTimeout(t);
  }, [fetchSuppliers]);

  const openDetail = async (s) => {
    try {
      const { data } = await api.get(`/marketplace/suppliers/${s.id}`);
      setActive(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const resetFilters = () => { setFilters({ category: "", specialty: "", country: "", emergency: false, min_rating: 0 }); setQ(""); };
  const hasFilters = q || filters.category || filters.specialty || filters.country || filters.emergency || filters.min_rating;

  if (authLoading) return null;
  if (!canSeeMarketplace(user)) return <Navigate to="/app" replace />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="marketplace-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-400">Seavanto network</div>
          <h1 className="font-display text-3xl sm:text-4xl">Supplier Marketplace</h1>
          <p className="text-sm text-zinc-500 mt-1">Discover trusted marine service providers for your fleet.</p>
        </div>
        {canManageMarketplace(user) && (
          <Button asChild variant="outline" data-testid="marketplace-admin-link">
            <Link to="/app/marketplace/admin"><Settings className="h-4 w-4 mr-2" />Manage marketplace</Link>
          </Button>
        )}
      </div>

      {/* Search bar */}
      <div className="mt-6 relative">
        <Search className="h-5 w-5 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search suppliers, services, brands…"
          className="pl-12 h-12 text-base rounded-full"
          data-testid="marketplace-search"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Filters */}
        <aside className="space-y-5" data-testid="marketplace-filters">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg">Filters</h3>
            {hasFilters && (
              <button onClick={resetFilters} className="text-xs text-zinc-500 hover:text-ink inline-flex items-center gap-1" data-testid="clear-filters">
                <X className="h-3 w-3" />Clear
              </button>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
              className="mt-1 w-full h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm"
              data-testid="filter-category"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">Specialty / Brand</label>
            <select
              value={filters.specialty}
              onChange={(e) => setFilters((f) => ({ ...f, specialty: e.target.value }))}
              className="mt-1 w-full h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm"
              data-testid="filter-specialty"
            >
              <option value="">All specialties</option>
              {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">Country</label>
            <Input value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))} placeholder="e.g. Spain" className="mt-1 h-9" data-testid="filter-country" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">Minimum rating</label>
            <div className="mt-1 flex gap-1">
              {RATING_OPTIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setFilters((f) => ({ ...f, min_rating: f.min_rating === r ? 0 : r }))}
                  className={`text-xs px-2 py-1 rounded-full border ${filters.min_rating === r ? "bg-ink text-white border-ink" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid={`filter-rating-${r}`}
                >{r}+</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={filters.emergency} onCheckedChange={(v) => setFilters((f) => ({ ...f, emergency: !!v }))} data-testid="filter-emergency" />
            <span className="text-sm text-zinc-700">24/7 Emergency only</span>
          </label>
        </aside>

        {/* Results */}
        <main>
          <div className="text-sm text-zinc-500 mb-3" data-testid="results-count">
            {loading ? "Loading…" : `${suppliers.length} supplier${suppliers.length === 1 ? "" : "s"}`}
          </div>
          {!loading && suppliers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-300 p-12 text-center text-zinc-500" data-testid="marketplace-empty">
              No suppliers match your search yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {suppliers.map((s) => <SupplierCard key={s.id} s={s} onOpen={openDetail} />)}
            </div>
          )}
        </main>
      </div>

      <SupplierDetail supplier={active} onClose={() => setActive(null)} />
    </div>
  );
}
